// HAFHTodoHome.jsx
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Loader from '../../components/utils/Loader';
import useFetch from '../../hooks/useFetch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMicrophone
  } from '@fortawesome/free-solid-svg-icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

import { Line } from 'react-chartjs-2';
import AILightComponent from '../../components/AILightComponent';
import SpeechInputComponent from '../../components/SpeechInputComponent';
import { GenerateLCCode } from '../../components/GenerateThink5whysCode';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

/* =========================
   MULTILINGUAL SUPPORT
========================= */
import HAFHTodoTranslations from './HAFHTodoTranslations';

const languages = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी' },
  { code: 'mr', label: 'मराठी' },
  { code: 'gu', label: 'ગુજરાતી' },
  { code: 'bn', label: 'বাংলা' },
  { code: 'ta', label: 'தமிழ்' },
  { code: 'te', label: 'తెలుగు' },
  { code: 'kn', label: 'ಕನ್ನಡ' },
  { code: 'ml', label: 'മലയാളം' },
  { code: 'pa', label: 'ਪੰਜਾਬੀ' },
  { code: 'as', label: 'অসমীয়া' },
];

export default function HAFHTodoPAHome() {

  const authState = useSelector(state => state.authReducer);
  const navigate = useNavigate();
  const [fetchData, { loading }] = useFetch();
  const [todos, setTodos] = useState([]);
  const [userId, setUserId] = useState(authState.user._id);
  const [userName, setUserName] = useState(authState.user.name);
  const [userCode, setUserCode] = useState(authState.user.think5whyscode);
  const [todoCode, setTodoCode] = useState(GenerateLCCode());
  
  const taskTitleRef = useRef(null);
  const listSectionRef = useRef(null);
  const createSectionRef = useRef(null);
  const reportsSectionRef = useRef(null);

  const [activeTab, setActiveTab] = useState('List');
  const [selectedLanguage, setSelectedLanguage] = useState('en');

  // Form States
  const [todoDate, setTodoDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDetails, setTaskDetails] = useState('');
  const [priority, setPriority] = useState("Medium");
  const [category, setCategory] = useState("Task");
  const [department, setDepartment] = useState("General");
  const [isDiary, setIsDiary] = useState(false);
  const [isRecreation, setIsRecreation] = useState(false);
  const [isProductivity, setIsProductivity] = useState(false);
  const [isHealth, setIsHealth] = useState(false);
  const [isExpense, setIsExpense] = useState(false);
  const [amount, setAmount] = useState('0.00');
  const [unit, setUnit] = useState('₹');
  const [isPinned, setIsPinned] = useState(false);
  const [isEscalated, setIsEscalated] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [linkedTodoId, setLinkedTodoId] = useState("");
  const [rcaId, setRcaId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [whoSuggested, setWhoSuggested] = useState("Self-created");
  const [dateFilter, setDateFilter] = useState("ALL"); // ALL | YESTERDAY | TODAY | TOMORROW | OTHER
  const [actionFilter, setActionFilter] = useState("ALL");
  const [status, setStatus] = useState("Planned");
  const [deferredDate, setDeferredDate] = useState("");

  // Editing State
  const [isEditing, setIsEditing] = useState(false);
  const [editingTodoId, setEditingTodoId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // UI States
  const [activeAssistantTodoId, setActiveAssistantTodoId] = useState(null);
  const [activeAssistantCreateTodo, setActiveAssistantCreateTodo] = useState(null);
  const [showMicrophoneAlert, setShowMicrophoneAlert] = useState(false);

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [isVoicePaused, setIsVoicePaused] = useState(false);
  const [recognitionInstance, setRecognitionInstance] = useState(null);
  const [isVoiceListening, setIsVoiceListening] = useState(false);
  const [isVoiceListeningPaused, setIsVoiceListeningPaused] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [activeTtsId, setActiveTtsId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isListSpeaking, setIsListSpeaking] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showAIHelp, setShowAIHelp] = useState(false);

  const t = (key) => HAFHTodoTranslations[selectedLanguage]?.[key] || key;

  const isSameDay = (d1, d2) =>
    new Date(d1).toDateString() === new Date(d2).toDateString();

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const getWeekRange = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diffToMonday = d.getDate() - day + (day === 0 ? -6 : 1);
    const start = new Date(d.setDate(diffToMonday));
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);

    return { start, end };
  };

  const getMonthRange = (date, offset = 0) => {
    const d = new Date(date);
    const start = new Date(d.getFullYear(), d.getMonth() + offset, 1);
    const end = new Date(d.getFullYear(), d.getMonth() + offset + 1, 0);
    return { start, end };
  };

  const getFilteredTodos = () => {
    const today = startOfDay(new Date());

    return todos.filter((todo) => {
      if (!todo.todoDate) return dateFilter === "ALL";

      const d = startOfDay(new Date(todo.todoDate));

      const diffDays = (d - today) / (1000 * 60 * 60 * 24);

      // basic filters
      if (dateFilter === "today") return diffDays === 0;
      if (dateFilter === "yesterday") return diffDays === -1;
      if (dateFilter === "tomorrow") return diffDays === 1;

      // week filters
      if (dateFilter === "thisweek") {
        const { start, end } = getWeekRange(new Date());
        return d >= start && d <= end;
      }

      if (dateFilter === "lastweek") {
        const { start, end } = getWeekRange(new Date());
        start.setDate(start.getDate() - 7);
        end.setDate(end.getDate() - 7);
        return d >= start && d <= end;
      }

      if (dateFilter === "nextweek") {
        const { start, end } = getWeekRange(new Date());
        start.setDate(start.getDate() + 7);
        end.setDate(end.getDate() + 7);
        return d >= start && d <= end;
      }

      // month filters
      if (dateFilter === "thismonth") {
        const { start, end } = getMonthRange(new Date());
        return d >= start && d <= end;
      }

      if (dateFilter === "lastmonth") {
        const { start, end } = getMonthRange(new Date(), -1);
        return d >= start && d <= end;
      }

      if (dateFilter === "nextmonth") {
        const { start, end } = getMonthRange(new Date(), 1);
        return d >= start && d <= end;
      }

      return true;
    });
  };

  const taskCount = todos.filter((a) => a?.category === "Task").length;
  const productivityCount = todos.filter((a) => a?.category === "Productivity").length;
  const healthCount = todos.filter((a) => a?.category === "Health").length;
  const recreationCount = todos.filter((a) => a?.category === "Recreation").length;
  const expensesCount = todos.filter((a) => a?.category === "Expenses").length;
  const othersCount = todos.filter((a) => a?.category === "Others").length;

  const STATUS_ORDER = [
    "Planned",
    "In Progress",
    "Done",
    "Deferred",
    "Cancelled",
  ];

  const groupByStatus = (list) => {
    const grouped = {};
    STATUS_ORDER.forEach((s) => (grouped[s] = []));
    list.forEach((todo) => {
      const status = todo.status || "Planned";
      if (!grouped[status]) grouped[status] = [];
      grouped[status].push(todo);
    });
    // sort inside each group by date/time
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort(
        (a, b) => new Date(b.todoDate) - new Date(a.todoDate)
      );
    });
    return grouped;
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setTimeout(() => {
      const sectionMap = {
        [t('list')]: listSectionRef,
        [t('create')]: createSectionRef,
        [t('reports')]: reportsSectionRef,
      };
      const selectedRef = sectionMap[tab];
      selectedRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

    }, 100);
  };

  const fetchMyTodos = async () => {
    try {
      const config = {
        url: "/hafhtodo/mytodos",
        method: "post",
        data: {
          userId: userId,
        },
        headers: {
          Authorization: authState.token,
        },
      };

      const response = await fetchData(config, { silent: true });
      if (response?.status) {
        const formattedTodos = response.tasks.map((todo) => ({
          id: todo._id,
          todoDate: todo.todoDate ? todo.todoDate.split("T")[0] : "",
          status: todo.status,
          title: todo.title,
          desc: todo.description,
          category: todo.category,
          department: todo.department,
          amount: todo.amount,
          unit: todo.unit,
          isDiary: todo.isDiary,
          isPinned: todo.isPinned,
          isEscalated: todo.isEscalated,
          isActive: todo.isActive,
          priority: todo.priority,
          tts: true,
          linkedTodoId: todo.linkedTodoId || "",
          rcaId: todo.rcaId || "",
          remarks: todo.remarks || "",
          whoSuggested: todo.whoSuggested || "Self-created",
          alerts: todo.alerts || {},
          tags: todo.tags || [],
        }));
        setTodos(formattedTodos);
      }
      } catch (error) {
        console.error("Fetch Todo Error:", error);
        showMessage("Failed to load Records", "error");
      }
  };

  const hasFetched = useRef(false);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    if (!userId) return;

    if (hasFetched.current) return;
    hasFetched.current = true;

    fetchMyTodos();
  }, [userId]);


  const tabs = [
    t('list'),
    t('create'),
    t('reports'),
  ];

  const speakText = (data) => {
    if (!window.speechSynthesis) {
      showMessage("Text to Speech is not supported in this browser", "error");
      return;
    }

    speechSynthesis.cancel();

    let text = "";

    // CASE 1: array of todos
    if (Array.isArray(data)) {
      text = data
        .map(t => `Title: ${t.title || ""}. Category: ${t.category || ""}. Description: ${t.desc || ""}.`)
        .join(" ");
    }

    // CASE 2: string list (your current button)
    else if (typeof data === "string") {
      text = data;
    }

    // CASE 3: single todo object
    else if (typeof data === "object" && data !== null) {
      text = `Title: ${data.title || ""}. Category: ${data.category || ""}. Description: ${data.desc || ""}.`;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = selectedLanguage === "hi" ? "hi-IN" : "en-US";
    utterance.rate = 1;
    utterance.pitch = 1;

    utterance.onstart = () => {
      setIsSpeaking(true);
      setIsPaused(false);
    };

    utterance.onend = () => {
      setIsSpeaking(false);
      setIsPaused(false);
      setActiveTtsId(null);
    };

    utterance.onerror = () => {
      setIsSpeaking(false);
      setIsPaused(false);
      setActiveTtsId(null);
      showMessage("Error occurred while speaking", "error");
    };

    speechSynthesis.speak(utterance);
  };

  const pauseSpeech = () => { speechSynthesis.pause(); setIsPaused(true); };
  const resumeSpeech = () => { speechSynthesis.resume(); setIsPaused(false); };
  const stopSpeech = () => {
    speechSynthesis.cancel();
    setIsSpeaking(false);
    setIsPaused(false);
    setActiveTtsId(null);
  };

  const showMessage = (text, type = "success") => {
    setMessage({ type, text });
  };

  const resetForm = () => {
    setTodoDate(null);
    setTaskTitle("");
    setTaskDetails("");
    setPriority("Medium");
    setCategory("Task");
    setDepartment("General");
    setIsDiary(false);
    setIsRecreation(false);
    setIsProductivity(false);
    setIsHealth(false);
    setIsExpense(false);
    setAmount(0.00);
    setUnit("₹");
    setIsPinned(false);
    setIsEscalated(false);
    setIsActive(true);
    setLinkedTodoId("");
    setRcaId("");
    setRemarks("");
    setWhoSuggested("Self-created");
    setStatus("Planned");
    setDeferredDate("");
    setIsEditing(false);
    setEditingTodoId(null);
    setIsSaved(false);
  };

  const handleEditTodo = (todo) => {
    setIsEditing(true);
    setEditingTodoId(todo.id);
    setTodoDate(todo.todoDate ? todo.todoDate.split("T")[0] : "");
    setTaskTitle(todo.title || "");
    setTaskDetails(todo.desc || "");
    setPriority(todo.priority || "Medium");
    setCategory(todo.category || "Task");
    setDepartment(todo.department || "General");
    setIsDiary(todo.isDiary || false);
    setIsRecreation(todo.setIsRecreation || false);
    setIsProductivity(todo.isProductivy || false);
    setIsHealth(todo.isHealth || false);
    setIsExpense(todo.isExpense || false);
    setAmount(todo.amount || 0.00);
    setUnit(todo.unit || "₹");
    setIsPinned(todo.isPinned || false);
    setIsEscalated(todo.isEscalated || false);
    setIsActive(todo.isActive || true);
    setLinkedTodoId(todo.linkedTodoId || "");
    setRcaId(todo.rcaId || "");
    setRemarks(todo.remarks || "");
    setWhoSuggested(todo.whoSuggested || "Self-created");
    setStatus(todo.status || "Planned");
    setDeferredDate(todo.deferredDate || "");
    setTimeout(() => {
      createSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      taskTitleRef.current?.focus();
    }, 200);
  };

  const [isSaved, setIsSaved] = useState(false);

  const handleSaveTodo = async (e) => {
    e.preventDefault();
    if (isSaving) return;
    
    const errors = {};

    if (!taskTitle.trim()) {
      errors.title = "Title is required";
    }

    if (!taskDetails.trim()) {
      errors.description = "Description is required";
    }
    if (!category) {
      errors.category = "Category is required";
    }
    if (!department) {
      errors.department = "Section is required";
    }

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      showMessage(
        Object.values(errors).join(" | "),
        "error"
      );
      return;
    }
    
    setIsSaving(true);
    try {
      const payload = {
        user: userId,
        userCode: userCode,
        todoCode: todoCode,
        todoDate: todoDate,
        title: taskTitle.trim(),
        description: taskDetails.trim(),
        priority,
        category: category || "Task",
        department: department || "General",
        isDiary: isDiary || false,
        isRecreation: isRecreation,
        isProductivity: isProductivity,
        isHealth: isHealth,
        isExpense: isExpense,
        amount: amount,
        unit: unit,
        isPinned: isPinned || false,
        isEscalated: isEscalated || false,
        isActive: isActive || true,
        linkedTodoId,
        rcaId,
        remarks,
        whoSuggested,
        status,
        deferredDate: status === "Deferred" ? deferredDate : null,
        isToDo: true,
        tags: [
          priority,
          department,
        ].filter(Boolean),
      };

      const config = {
        url: "/hafhtodo/createtodo",
        method: "post",
        data: payload,
        headers: {
          Authorization: authState.token,
        },
      };
      // const response = await fetchData(config);
      const response = await fetchData(config, { silent: true });
      if (response?.status) {
        await fetchMyTodos();
        showMessage("Record Saved Successfully");
        setTodoCode(GenerateLCCode());
        setIsSaved(true);
        resetForm();
      }
    } catch (error) {
        console.error("FULL ERROR:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to save Data!";
        showMessage(errorMessage, "error");
    } finally {
    setIsSaving(false);
  }
  };

  const handleUpdateTodo = async () => {
    if (!taskTitle.trim() || !taskDetails.trim()) {
      showMessage("Title & Description are required", "error");
      return;
    }
    try {
      const payload = {
        todoDate: todoDate,
        title: taskTitle,
        description: taskDetails,
        priority,
        category: category || "Task",
        department: department || "General",
        isDiary: isDiary,
        isRecreation: isRecreation,
        isProductivity: isProductivity,
        isHealth: isHealth,
        isExpense: isExpense,
        amount: amount,
        unit: unit,
        isPinned: isPinned,
        isEscalated: isEscalated,
        isActive: isActive,
        linkedTodoId,
        rcaId,
        remarks,
        whoSuggested: whoSuggested || "Self-created",
        status,
        deferredDate: status === "Deferred" ? deferredDate : null,
        tags: [
          priority,
          department,
        ].filter(Boolean),
      };
      const config = {
        url: `/hafhtodo/${editingTodoId}`,
        method: "put",
        data: payload,
        headers: {
          Authorization: authState.token,
        },
      };
      // const response = await fetchData(config);
      const response = await fetchData(config, { silent: true });
      if (response?.status) {
        setTodos((prev) =>
          prev.map((todo) =>
            todo._id === editingTodoId
              ? { ...todo, ...payload }
              : todo
          )
        );
        await fetchMyTodos();
        showMessage("Record Updated Successfully");
        setIsSaved(true);
        resetForm();
      }
    } catch (error) {
      console.error(error);
      showMessage(
        error?.response?.data?.msg ||
        error?.message ||
        "Failed to update Record",
        "error"
      );
    }
  };

  useEffect(() => {
    if (!userId) return;

    if (hasFetched.current) return;
    hasFetched.current = true;

    fetchMyTodos();
  }, [userId]);

  const handleDeleteTodo = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      const config = {
        url: `/hafhtodo/${editingTodoId}`,
        method: "delete",
        headers: {
          Authorization: authState.token,
        },
      };
      // const response = await fetchData(config);
      const response = await fetchData(config, { silent: true });
      if (response?.status) {
        await fetchMyTodos();
        resetForm();
        showMessage("Record Deleted Successfully");
      }
    } catch (error) {
      console.error("DELETE TODO ERROR:", error);
      showMessage(
        error?.response?.data?.msg ||
        error?.message ||
        "Failed to delete To-Do",
        "error"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelTodo = () => resetForm();

  const toggleVoiceInput = () => {
    if (isVoiceListening) {
      stopVoiceInput();
      return;
    }
    startVoiceInput();
  };

  const startVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      showMessage("Speech Recognition not supported in this browser", "error");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN";           
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      if (transcript) {
        setTaskDetails(prev => prev + " " + transcript);
      }
    };

    recognition.onerror = (event) => {
      console.error(event);
      if (event.error !== "no-speech") {
        showMessage("Voice recognition error. Please try again.", "error");
        stopVoiceInput();
      }
    };

    recognition.onend = () => {
      setIsVoiceActive(false);
      setIsVoicePaused(false);
    };

    try {
      recognition.start();
      setRecognitionInstance(recognition);
      setIsVoiceActive(true);
      setIsVoicePaused(false);
      
      // Auto focus on textarea
      document.getElementById("taskDetails")?.focus();
      
      showMessage("🎤 Listening... Speak now", "success");
    } catch (err) {
      showMessage("Failed to start voice input", "error");
    }
  };

  const pauseVoiceInput = () => {
    if (recognitionInstance) recognitionInstance.stop();
    setIsVoicePaused(true);
    showMessage("Voice input paused", "success");
  };

  const resumeVoiceInput = () => {
    setIsVoicePaused(false);
    startVoiceInput();
  };

  const stopVoiceInput = () => {
    if (recognitionInstance) recognitionInstance.stop();
    setIsVoiceActive(false);
    setIsVoicePaused(false);
    setRecognitionInstance(null);
    showMessage("Voice input stopped", "success");
  };

  const handleMicrophoneClick = () => {
    setShowMicrophoneAlert(true);
  };

  const buildSingleTodoSpeech = (todo) => {
    const parts = [];
    parts.push(`Let me read out this chronicle or task to you.`);
    if (todo?.category) parts.push(`Task Category is ${todo.category}.`);
    if (todo?.title) parts.push(`Task title is ${todo.title}.`);
    if (todo?.desc) parts.push(`The description is ${todo.desc}.`);
    return parts.join(" ");
  };

  const filtered = getFilteredTodos();
  const totalTasks = filtered.length;
  const completedTasks = filtered.filter(t => t.status === "Done").length;
  const pendingTasks = totalTasks - completedTasks;
  const rcaEntries = filtered.filter(t => t.rcaId && t.rcaId.trim() !== "").length;
  const completionRatio = totalTasks ? Math.round((completedTasks / totalTasks) * 100): 0;
 
  const grouped = groupByStatus(filtered);

  // ====================== CHART ======================
  const chartData = useMemo(() => ({
    labels: [t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')],
    datasets: [
      {
        label: t('taskCompletionPercent'),
        data: [65, 72, 70, 80, 82, 88],
        borderColor: '#14b8a6',
        backgroundColor: 'rgba(20,184,166,0.18)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: '#fde68a',
      },
    ],
  }), [selectedLanguage]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: '#fde68a' } },
    },
    scales: {
      x: { ticks: { color: '#d1d5db' }, grid: { color: 'rgba(255,255,255,0.05)' } },
      y: { beginAtZero: true, ticks: { color: '#d1d5db', stepSize: 20 }, grid: { color: 'rgba(255,255,255,0.05)' } },
    },
  }), []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-slate-950 to-slate-900 text-gray-100">
      <div className="mx-auto max-w-7xl space-y-2">
        <div className="overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-r from-black via-slate-950 to-slate-900 p-5 shadow-2xl sm:p-8">
          <div className="flex flex-col gap-2 justify-start lg:flex-row">
            <div className="flex-1 text-left justify-between">
              <div className="mb-2 flex flex-row gap-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-3 py-1 text-xs lg:text-sm text-gray-200 backdrop-blur-sm">
                  <i className="fa-solid fa-bolt text-yellow-300"></i>
                  {t("dailyChronicle")}
                </div>
                <div className="flex flex-col gap-2 px-3 py-1">
                  <select
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                    className="w-full sm:w-48 rounded-sm border border-slate-700 bg-slate-900 px-3 py-1 text-xs lg:text-sm text-white outline-none transition-all focus:border-teal-500"
                  >
                    {languages.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <h1 className="text-left text-xl lg:text-2xl font-black leading-tight text-yellow-200">PersonalSpace</h1>
              <p className="mt-2 text-left text-xs lg:text-sm  leading-7 text-gray-300">{t('heroDescription')}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="rounded-3xl border border-slate-800 bg-slate-900 p-3 shadow-xl">
          <div className="grid grid-cols-3 gap-2 lg:grid-cols-6">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`flex w-full items-center justify-center rounded-md px-2 py-1 text-center text-xs lg:text-sm  font-semibold transition-all ${
                  activeTab === tab ? 'bg-teal-700 text-yellow-200 shadow-lg shadow-teal-900/30' : 'bg-slate-800 text-gray-200 hover:bg-slate-700 hover:text-yellow-200'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-5 xl:grid-cols-3">
          <div className="space-y-5 xl:col-span-2">
            {/* ====================== TODO LIST ====================== */}
            <div ref={listSectionRef} className="rounded-xl border border-slate-800 bg-slate-700 p-2 shadow-2xl">
              <div className="mb-2 flex flex-col gap-4">
                <div>
                  <h2 className="text-lg lg:text-xl font-black text-yellow-200">{t('todoList')}</h2>
                  <p className="mt-1 text-sm text-gray-300">{t('todoListDesc')}</p>
                </div>
                <div className="mb-1 grid grid-cols-3 lg:grid-cols-5 gap-1 space-y-1 border-b border-cyan-300 pb-2">
                  {[
                    { key: "ALL", label: t("ALL"), count: todos.length },
                    { key: "Task", label: t("Task"), count: taskCount },
                    { key: "Productivity", label: t("Productivity"), count: productivityCount },
                    { key: "Health", label: t("Health"), count: healthCount },
                    { key: "Recreation", label: t("Recreation"), count: recreationCount },
                    { key: "Expenses", label: t("Expenses"), count: expensesCount },
                    { key: "Others", label: t("Others"), count: othersCount },
                  ].map((af) => (
                    <button
                      key={af.key}
                      onClick={() => setActionFilter(af.key)}
                      className={`rounded-md px-2 py-1 text-xs lg:text-sm transition-all flex items-center justify-between gap-2
                        ${
                          actionFilter === af.key
                            ? "bg-teal-600 text-white"
                            : "bg-slate-800 text-gray-300 hover:bg-slate-700"
                        }`}
                    >
                      <span>{af.label}</span>
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded-full
                          ${
                            actionFilter === af.key
                              ? "bg-white/20 text-white"
                              : "bg-slate-700 text-cyan-300"
                          }`}
                      >
                        {af.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-1 grid grid-cols-3 lg:grid-cols-6 gap-1 space-y-1 pb-2">
                {[
                  "all",
                  "today",
                  "yesterday",
                  "tomorrow",
                  "thisweek",
                  "lastweek",
                  "nextweek",
                  "thismonth",
                  "lastmonth",
                  "nextmonth",
                ].map((f) => (
                  <button
                    key={f}
                    onClick={() => setDateFilter(f)}
                    className={`rounded-md px-1 py-1 text-xs lg:tex-sm transition-all
                      ${dateFilter === f
                        ? "bg-teal-600 text-white"
                        : "bg-slate-800 text-gray-300 hover:bg-slate-700"
                      }`}
                  >
                    {t(f)}
                  </button>
                ))}
                <button 
                  onClick={() => { 
                    setActiveTab(t('create'));
                    setTimeout(() => {
                      createSectionRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });

                    }, 100);
                }}
                  className="w-24 lg:w-32 text-xs lg:text-sm rounded-md bg-blue-700 px-1 py-2 font-bold text-white shadow-lg transition-all hover:bg-blue-600"
                >
                  <i className="fa-solid fa-plus mr-2"></i>{t('addTask')}
                </button>
              </div>
              {/* Stats */}
              <div className="grid grid-cols-3 gap-1 lg:grid-cols-5">
                {[
                  {
                    title: t('pendingTasks'),
                    value: pendingTasks,
                    icon: 'fa-list-check',
                    growth: null,
                  },
                  {
                    title: t('taskCompletionRatio'),
                    value: `${completionRatio}%`,
                    icon: 'fa-chart-line',
                    growth: null,
                  },
                  {
                    title: t('rcaEntries'),
                    value: rcaEntries,
                    icon: 'fa-chart-column',
                    growth: null,
                  },
                ].map((stat) => (
                  <div key={stat.title} className="rounded-xl border border-slate-800 bg-slate-900 p-4 shadow-xl transition-all hover:-translate-y-1 hover:border-teal-500">
                    <div className="flex items-start justify-between">
                      <div className="rounded-2xl bg-slate-800 p-3 text-yellow-200">
                        <i className={`fa-solid ${stat.icon} text-lg`}></i>
                      </div>
                      <span className="rounded-full bg-emerald-700/20 px-2 py-1 text-xs lg:text-sm font-semibold text-emerald-300">{stat.growth}</span>
                    </div>
                    <h2 className="mt-2 text-xs lg:text-sm font-black text-yellow-200 sm:text-3xl">{stat.value}</h2>
                    <p className="mt-1 text-xs lg:text-sm text-gray-300">{stat.title}</p>
                  </div>
                ))}
              </div>

              <div className='flex justify-end'>
                {!isListSpeaking ? (
                  <button
                    onClick={() => {
                      setIsListSpeaking(true);
                      const speechText = filtered
                        .map((todo, index) => {
                          return `
                            Task ${index + 1}.
                            Current Status: ${todo.status || "Not specified"}.
                            Title: ${todo.title || "Not specified"}.
                            Under Category: ${todo.category || "Not specified"}.
                          `;
                        })
                        .join(" ");

                      speakText(speechText);
                    }}
                    className="w-64 rounded-md bg-yellow-600 px-3 py-2 text-xs lg:text-sm font-bold text-black hover:bg-yellow-500"
                  >
                    <i className="fa-solid fa-volume-high mr-2"></i>
                    {t("listenList")}
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => { speechSynthesis.pause(); setIsPaused(true);}}
                      className="rounded-xl bg-blue-600 px-3 py-1 text-xs lg:text-sm font-bold text-white"
                    >
                      <i className="fa-solid fa-pause mr-1"></i> Pause
                    </button>
                    <button
                      onClick={() => {speechSynthesis.resume();setIsPaused(false);}}
                      className="rounded-xl bg-yellow-600 px-3 py-1 text-xs lg:text-sm font-bold text-black"
                    >
                      <i className="fa-solid fa-play mr-1"></i> Resume
                    </button>

                    <button
                      onClick={() => {
                        speechSynthesis.cancel();
                        setIsListSpeaking(false);
                        setIsPaused(false);
                        setActiveTtsId(null);
                      }}
                      className="rounded-xl bg-red-600 px-3 py-1 text-xs lg:text-sm font-bold text-white"
                    >
                      <i className="fa-solid fa-stop mr-1"></i> Stop
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {filtered.map((todo) => (
                  <div key={todo._id} className="rounded-3xl border border-slate-800 bg-slate-800 p-5 transition-all hover:border-teal-500 hover:shadow-lg">
                    <div className="flex flex-col gap-4">
                      <div className="flex-1">
                        <div className="mb-2 text-xs text-teal-300">
                          <i className="fa-regular fa-calendar mr-1"></i>
                          {todo.todoDate
                            ? new Date(todo.todoDate).toLocaleDateString("en-GB")
                            : "No Date"}
                          <span className="rounded-full bg-sky-700/20 px-3 py-1 text-xs lg:text-sm font-semibold text-sky-300">
                            {todo.status}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-xs lg:text-lg font-bold text-yellow-200">{todo.title}</h3>
                          {todo.tts && (
                            <span className="rounded-full bg-orange-700/20 px-3 py-1 text-xs lg:text-sm font-semibold text-orange-300">
                              <i className="fa-solid fa-volume-high mr-1"></i>{t('ttsEnabled')}
                            </span>
                          )}
                          <span className="rounded-full bg-sky-700/20 px-3 py-1 text-xs lg:text-sm font-semibold text-sky-300">
                            {todo.category}
                          </span>
                        </div>
                        <p className="mt-3 w-full block text-sm leading-7 text-gray-100 bg-gray-700 whitespace-pre-wrap break-words p-4 rounded-xl">
                          {todo.desc}
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2">
                          {todo.tags?.map((tag, i) => (
                            <span key={i} className="rounded-full bg-slate-800 px-3 py-1 text-xs lg:text-sm font-medium text-gray-100">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="flex gap-2 bg-gray-800 border border-gray-600">
                        <div className={`flex gap-2 ${isListSpeaking ? "opacity-40 pointer-events-none" : ""}`}>
                          {activeTtsId !== todo._id ? (
                            <button onClick={() => {
                                setIsListSpeaking(true);
                                setActiveTtsId(todo._id);
                                const speechText = buildSingleTodoSpeech(todo);
                                speakText(speechText);
                              }} 
                              className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-slate-800 px-1 py-1 text-yellow-200 hover:bg-teal-700">
                              <i className="fa-solid fa-play"></i>
                            </button>
                          ) : isPaused ? (
                            <button onClick={resumeSpeech} className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-yellow-700 px-1 py-1 text-black hover:bg-yellow-600">
                              <i className="fa-solid fa-play"></i>
                            </button>
                          ) : (
                            <>
                              <button onClick={pauseSpeech} className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-blue-700 px-1 py-1 text-white hover:bg-blue-600">
                                <i className="fa-solid fa-pause"></i>
                              </button>
                              <button onClick={stopSpeech} className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-red-700 px-1 py-1 text-white hover:bg-red-600">
                                <i className="fa-solid fa-stop"></i>
                              </button>
                            </>
                          )}
                        </div>
                        <button onClick={() => handleEditTodo(todo)} className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-slate-800 px-1 py-1 text-yellow-200 hover:bg-teal-700">
                          <i className="fa-solid fa-pen"></i>
                        </button>
                        <button
                          onClick={() => setActiveAssistantTodoId(prev => prev === todo._id ? null : todo._id)}
                          className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-slate-800 px-1 py-1 text-yellow-200 hover:bg-teal-700"
                        >
                          <i className="fa-solid fa-robot"></i>
                        </button>
                      </div>
                    </div>

                    {activeAssistantTodoId === todo._id && (
                      <div className="mt-2">
                        <AILightComponent
                          param1={"hafh_stakeholder_task_analysis"}
                          param2={`To-Do Title: ${todo.title} Category: ${todo.category} Section: ${todo.department} Priority: ${todo.priority} Description: ${todo.desc}`}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ====================== CREATE / EDIT FORM ====================== */}
            <div ref={createSectionRef} className="rounded-3xl border border-slate-800 bg-slate-900 p-5 shadow-2xl sm:p-6">
              <div className="mb-2 flex items-start gap-4">
                <div className="rounded-2xl bg-slate-800 p-4 text-yellow-200">
                  <i className="fa-solid fa-square-plus text-lg lg:text-xl"></i>
                </div>
                <div>
                  <h2 className="text-lg lg:text-xl font-black text-yellow-200">
                    {isEditing ? "Edit To-Do" : t("createTodo")}
                  </h2>
                  <p className="mt-1 text-xs lg:text-sm text-gray-300">
                    {isEditing ? "Update the selected task" : t("createTodoDesc")}
                  </p>
                </div>
              </div>

              <div className="relative overflow-hidden rounded-2xl border border-cyan-400/40 bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-950 p-4 shadow-lg shadow-cyan-900/20">
                <div className="absolute -top-10 -right-10 h-24 w-24 rounded-full bg-cyan-500/10 blur-2xl"></div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-cyan-700 text-white">
                    <i className="fa-solid fa-link"></i>
                  </div>
                  <div>
                    <div className="text-xs lg:text-sm font-bold tracking-wide text-cyan-200">
                      {t("todoCode")}
                    </div>
                    <div className="text-[10px] lg:text-xs text-gray-400">
                      Intelligent Task Chain Identifier
                    </div>
                  </div>
                </div>
                <div className="rounded-xl border border-cyan-500/20 bg-slate-950/70 px-4 py-3">
                  <div className="text-lg lg:text-2xl font-extrabold tracking-widest text-yellow-300 break-all">
                    {todoCode}
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-xs lg:text-sm text-gray-300 leading-relaxed">
                  <p className='text-orange-300 text-xs lg:text-sm'>
                    This is an important reference code that helps Think5Whys create, connect, identify, and analyze linked chains of tasks, activities,
                    actions, dependencies, and workflows over time.
                  </p>
                  <p className="text-cyan-300 text-xs lg:text-sm">
                    Even without AI assistance, this code enables structured tracking, historical mapping, operational visibility, accountability,
                    and relationship discovery between connected tasks.
                  </p>
                  <p className="text-orange-300 text-xs lg:text-sm">
                    With AI-powered analysis, these connected task chains can generate deep insights related to productivity, delays, execution quality,
                    bottlenecks, behavioral patterns, operational intelligence, and future recommendations.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <div className='border border-blue-100 p-4 rounded-xl'>
                  <div>
                    <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                      {t("todoDate")}*
                    </label>

                    <input
                      type="date"
                      value={todoDate}
                      onChange={(e) => setTodoDate(e.target.value)}
                      className="w-40 rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none transition-all focus:border-teal-500 focus:ring-4 focus:ring-teal-900/40"
                    />
                  </div>

                  <div className='grid grid-cols-1 lg:grid-cols-2 gap-2'>
                    <div className="flex flex-col">
                      <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                        {t("status")}*
                      </label>
                      <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="w-32 rounded-2xl border border-slate-700 bg-slate-950 px-4 py-1 text-gray-100 outline-none focus:border-teal-500"
                      >
                        <option value="Planned">Planned</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Done">Done</option>
                        <option value="Deferred">Deferred</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </div>
                    {status === "Deferred" && (
                      <div className="flex flex-col">
                        <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                          {t("deferred_date")}
                        </label>

                        <input
                          type="date"
                          value={deferredDate}
                          onChange={(e) => setDeferredDate(e.target.value)}
                          className="w-32 rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500"
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">{t("title")}*</label>
                    <input
                      type="text"
                      ref={taskTitleRef}
                      value={taskTitle}
                      onChange={(e) => setTaskTitle(e.target.value)}
                      placeholder={"Enter Title"}
                      style={{width: "100%"}}
                      className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none transition-all focus:border-teal-500 focus:ring-4 focus:ring-teal-900/40"
                    />
                  </div>
                  <div className='grid grid-cols-1 lg:grid-cols-3 gap-2'>
                    <div className='flex flex-col space-y-1'>
                      <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">{t("priority")}*</label>
                      <select
                        value={priority}
                        onChange={(e) => setPriority(e.target.value)}
                        className="w-32 rounded-2xl border border-slate-700 bg-slate-950 px-4 py-1 text-gray-100 outline-none focus:border-teal-500"
                      >
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                    </div>
                    <div className='flex flex-col space-y-1'>
                      <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                        Category*
                      </label>

                      <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="w-64 rounded-2xl border border-slate-700 bg-slate-950 px-4 py-1 text-gray-100 outline-none focus:border-teal-500"
                      >
                        <option value="Task">Task</option>
                        <option value="Health">Health</option>
                        <option value="Productivity">Productivity</option>
                        <option value="Recreation">Recreation</option>
                        <option value="Expenses">Expenses</option>
                        <option value="Others">Others</option>
                      </select>
                    </div>

                    {category === "Task" && (
                      <div className='flex flex-col space-y-1'>
                        <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                          {t("section")}*
                        </label>

                        <select
                          value={department}
                          onChange={(e) => setDepartment(e.target.value)}
                          className="w-64 rounded-2xl border border-slate-700 bg-slate-950 px-4 py-1 text-gray-100 outline-none focus:border-teal-500"
                        >
                          <option value="GENERAL">General</option>
                          <option value="HOSTEL_ADMIN">Hostel Administration</option>
                          <option value="WARDEN_OFFICE">Warden Office</option>
                          <option value="ROOM_MANAGEMENT">Room / Bed Allocation</option>
                          <option value="HOUSEKEEPING">Housekeeping & Cleanliness</option>
                          <option value="MESS_FOOD">Mess / Food Services</option>
                          <option value="FOOD_QUALITY">Food Quality & Feedback</option>
                          <option value="ATTENDANCE">Attendance & Discipline</option>
                          <option value="SECURITY">Security & Safety</option>
                          <option value="EMERGENCY">Emergency / SOS Handling</option>
                          <option value="STUDENT_SUPPORT">Student Support Desk</option>
                          <option value="GRIEVANCE">Grievance / Complaint Cell</option>
                          <option value="SPORTS">Sports & Recreation</option>
                          <option value="STUDY_SUPPORT">Study / Academic Support</option>
                          <option value="TRANSPORT">Transport / Pickup-Drop</option>
                          <option value="MAINTENANCE">Maintenance & Repairs</option>
                          <option value="FINANCE">Fees & Payments</option>
                          <option value="INVENTORY">Inventory & Supplies</option>
                          <option value="COMMUNICATION">Notices & Communication</option>
                          <option value="DIGITAL_SYSTEM">App / System Support</option>
                          <option value="OTHERS">Others</option>
                        </select>
                      </div>
                    )}
                  </div>
                  {category === "EXPENSES" && (
                    <div className="grid grid-cols-1 lg:flex-cols-2 gap-2">
                      <div className="flex flex-col lg:flex-row gap-1">
                        <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                          Amount*
                        </label>
                        <input
                          type="number"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          placeholder="Enter Amount"
                          className="w-40 rounded-2xl border border-slate-700 bg-slate-950 px-4 py-1 text-gray-100 outline-none focus:border-teal-500"
                        />
                      </div>
                      <div className="flex flex-col lg:flex-row gap-1">
                        <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                          Currency
                        </label>

                        <input
                          type="text"
                          value={currency}
                          onChange={(e) => setCurrency(e.target.value)}
                          placeholder="Rs."
                          className="w-32 rounded-2xl border border-slate-700 bg-slate-950 px-4 py-1 text-gray-100 outline-none focus:border-teal-500"
                        />
                      </div>
                    </div>
                  )}
                  <div className='flex flex-row gap-2 mt-4'>
                    <p className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">{t("details")}*</p>
                    <input 
                      type="checkbox" 
                      className="mr-2 cursor-pointer"
                      checked={showMicrophoneAlert} 
                      onChange={() => setShowMicrophoneAlert(!showMicrophoneAlert)} 
                    />
                    <span className="text-xs lg:text-sm font-medium text-gray-100">{t("evi")}</span>
                    <FontAwesomeIcon icon={faMicrophone} className="text-yellow-500 text-lg lg:text-xl ml-3" />
                  </div>
                  <div className="mb-2 text-xs lg:text-sm text-white">
                      { showMicrophoneAlert ? (
                          <SpeechInputComponent
                            label="Details"
                            name="taskDetails"
                            value={taskDetails}
                            onChange={(e) => setTaskDetails(e.target.value)}
                            placeholder="Click Mic and Speak or Talk for Voice Input..."
                            maxLength="2000"
                          />
                      ) : (
                        <textarea 
                          type="input" 
                          name="taskDetails" 
                          id="taskDetails" 
                          value={taskDetails}
                          placeholder={showMicrophoneAlert ? "Write here or Click Mic for Voice Input...":"Write here.."}
                          onChange={(e) => setTaskDetails(e.target.value)}
                          maxLength="2000"
                          style={{
                            border: '1px solid #ccc',
                            padding: '2px',
                            backgroundColor: '#000000', 
                            color: '#f0f0f0',
                            maxWidth: '100%', 
                            maxHeight: '150px' 
                          }}
                        />
                      )}
                  </div>

                  <div className="flex flex-wrap items-start gap-3">
                    <button
                      onClick={() => setActiveAssistantCreateTodo(!activeAssistantCreateTodo)}
                      className="rounded-2xl bg-teal-700 px-2 py-3 font-bold text-white shadow-lg transition-all hover:bg-teal-600 w-40 lg:w-48 text-xs lg:text-sm"
                    >
                      <i className="fa-solid fa-wand-magic-sparkles mr-2"></i>
                      {t("refineWithAI")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAIHelp((prev) => !prev)}
                      className="h-10 w-10 rounded-full bg-slate-800 text-yellow-300 hover:bg-slate-700 transition-all flex items-center justify-center"
                    >
                      <i className="fa-solid fa-circle-question text-lg"></i>
                    </button>
                  </div>

                  {showAIHelp && (
                    <div className="mt-3 rounded-2xl border border-yellow-700 bg-slate-900 p-4 text-xs lg:text-sm text-gray-200 space-y-3">
                      <div className="text-yellow-200 font-bold">
                        How “Refine With AI” Works
                      </div>
                      <div>
                        Think5Whys can generate a structured AI-ready prompt for your task, complaint, idea, productivity note, recreation plan, expense entry,
                        or workflow requirement.
                      </div>
                      <div>
                        You can copy that generated prompt and paste it into any GenAI tool of your choice to improve, rewrite, structure, summarize,
                        expand, or optimize your description.
                      </div>
                      <div className="text-cyan-300">
                        Supported / Recommended AI Tools:
                      </div>
                      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 text-gray-100">
                        <div>• ChatGPT</div>
                        <div>• Claude</div>
                        <div>• Gemini</div>
                        <div>• Grok</div>
                        <div>• Perplexity</div>
                        <div>• Microsoft Copilot</div>
                        <div>• DeepSeek</div>
                        <div>• Meta AI</div>
                        <div>• Poe</div>
                        <div>• You.com AI</div>
                        <div>• Think5Whys Own AI</div>
                      </div>
                      <div>
                        Once you are satisfied with the AI-generated output, copy the improved response from that AI tool and paste it back into
                        the Think5Whys description field for better quality inputs, task clarity, automation accuracy, analytics, and execution support.
                      </div>
                      <div className="text-teal-300">
                        You can also take advantage of optional auto-integration workflows with your preferred GenAI tools using a seamless copy + paste assisted flow,
                        where Think5Whys can help send the generated prompt and receive the refined response more efficiently. This feature is paid and usage cost
                        may apply as per the active pricing plan of the selected GenAI tool. Pricing can vary across different AI providers and models.
                      </div>
                      <div className="text-orange-300">
                        Tip: More detailed and structured descriptions help Think5Whys workflows, AI analysis, task assignment, and reporting systems
                        perform better.
                      </div>
                    </div>
                  )}
                </div>

                {/* AI Refinement Section */}
                {activeAssistantCreateTodo && (
                  <>
                    {(!taskTitle.trim() || !taskDetails.trim() || !priority || !category || !department) ? (
                      <div className="mt-4 rounded-2xl border border-red-500 bg-red-500/10 p-4">
                        <div className="flex items-start gap-3 text-red-300">
                          <i className="fa-solid fa-circle-exclamation mt-1"></i>
                          <div>
                            <p className="font-bold text-yellow-200">{t("missingri")}</p>
                            
                            <p className="mt-1 text-xs lg:text-sm text-red-200">
                              {t("plfillthefield")}{ 
                                [taskTitle.trim(), taskDetails.trim(), priority, category, department].filter(Boolean).length === 3 ? '' : 's'
                              }:
                            </p>

                            <ul className="mt-2 text-xs lg:text-sm text-red-200 [&_li]:text-gray-100 list-disc list-inside space-y-1">
                              {!taskTitle.trim() && <li className='text-xs lg:text-sm'><strong>{t("title")}</strong></li>}
                              {!taskDetails.trim() && <li className='text-xs lg:text-sm'><strong>{t("description")}</strong></li>}
                              {!priority && <li className='text-xs lg:text-sm'><strong>{t("priority")}</strong></li>}
                              {!category && <li className='text-xs lg:text-sm'><strong>{t("category")}</strong></li>}
                              {!department && <li className='text-xs lg:text-sm'><strong>{t("section")}</strong></li>}
                            </ul>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2">
                        <AILightComponent
                          param1="Hostel / PG / Dormitory Task / Chronicle / ToDo"
                          param2={`
                              Title: ${taskTitle}
                              Department: ${department}
                              Category: ${category}
                              Priority: ${priority}
                              Description: ${taskDetails}
                            `}
                        />
                      </div>
                    )}
                  </>
                )}

                <div className="mt-4">
                  <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                    {t("whoDrafted")}
                  </label>
                  <input
                    type="text"
                    value={whoSuggested}
                    onChange={(e) => setWhoSuggested(e.target.value)}
                    placeholder="e.g. Grok, ChatGPT, Claude, Self, Manager, etc."
                    className="w-full text-xs lg:text-sm rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-900/40"
                  />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
                  <div className="gap-2">
                    <input 
                      type="checkbox" 
                      className="mr-2 cursor-pointer"
                      checked={isDiary} 
                      onChange={(e) => setIsDiary(e.target.checked)} 
                    />
                    <label className="mb-2 text-xs lg:text-sm font-bold text-yellow-200">Send to My Diary</label>                  
                  </div>
                  <div className="gap-2">
                    <input 
                      type="checkbox" 
                      className="mr-2 cursor-pointer"
                      checked={isPinned} 
                      onChange={(e) => setIsPinned(e.target.checked)} 
                    />
                    <label className="mb-2 text-xs lg:text-sm font-bold text-yellow-200">Pinned as Important</label>
                  </div>
                  <div className='gap-2'>
                    <input 
                      type="checkbox" 
                      className="mr-2 cursor-pointer"
                      checked={isActive} 
                      onChange={(e) => setIsActive(e.target.checked)} 
                    />
                    <label className="mb-2 text-xs lg:text-sm font-bold text-yellow-200">Is Active (Not Visible)</label>
                  </div>
                </div>
                <div className='gap-2'>
                  <input 
                    type="checkbox" 
                    className="mr-2 cursor-pointer"
                    checked={isEscalated} 
                    onChange={(e) => setIsEscalated(e.target.checked)} 
                  />
                  <label className="mb-2 text-xs lg:text-sm font-bold text-yellow-200">{t("etohadmin")}</label>
                </div>

                <div className="grid gap-1 grid-cols-1 lg:grid-cols-2 border border-blue-100 p-4 rounded-xl">
                  <div className='flex flex-col space-y-1'>
                    <label className="mb-2 text-xs lg:text-sm font-bold text-yellow-200">Linked Record ID</label>
                    <input type="text" value={linkedTodoId} onChange={(e) => setLinkedTodoId(e.target.value)} placeholder="Enter Linked Record ID" className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500" />
                  </div>
                  <div className='flex flex-col space-y-1'>
                    <label className="mb-2 text-xs lg:text-sm font-bold text-yellow-200">RCA ID</label>
                    <input type="text" value={rcaId} onChange={(e) => setRcaId(e.target.value)} placeholder="Enter RCA ID" className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500" />
                  </div>
                </div>

                <div>
                  <label className="mb-2 text-xs lg:text-sm font-bold text-yellow-200">Additional Remarks</label>
                  <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Enter Remarks" style={{ height: "150px" }} className="w-full text-xs lg:text-sm rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500" />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-2 grid grid-cols-4 lg:grid-cols-4 gap-1">
                <button
                  disabled={isEditing || isSaving || isSaved}
                  onClick={handleSaveTodo}
                  className={`w-40 lg:w-48 text-xs lg:text-sm rounded-xl px-2 py-1 font-bold text-white shadow-lg transition-all
                    ${(isEditing || isSaving)
                      ? "cursor-not-allowed bg-slate-700 opacity-50"
                      : "bg-teal-600 hover:bg-teal-500"
                    }`}
                >
                  <i className="fa-solid fa-floppy-disk mr-2"></i>
                  {isSaving ? "Saving..." : "Save Data"}
                </button>

                {isEditing && (
                  <>
                    <button onClick={() => handleUpdateTodo()} className="w-20 lg:w-32 text-xs lg:text-sm rounded-xl bg-yellow-600 px-2 py-1 font-bold text-black shadow-lg hover:bg-yellow-500">
                      <i className="fa-solid fa-rotate mr-2"></i> Update
                    </button>
                    <button onClick={handleDeleteTodo} disabled={isDeleting} className="w-20 lg:w-32 text-xs lg:text-sm rounded-xl bg-red-700 px-2 py-1 font-bold text-white shadow-lg hover:bg-red-600">
                      <i className="fa-solid fa-trash mr-2"></i> Delete
                    </button>
                  </>
                )}

                <button onClick={handleCancelTodo} className="w-20 lg:w-32 text-xs lg:text-sm rounded-xl bg-slate-700 px-2 py-1 font-bold text-white shadow-lg hover:bg-slate-600">
                  <i className="fa-solid fa-xmark mr-2"></i> Cancel
                </button>
              </div>
              {/* Message Banner */}
              {message.text && (
                <div className={`rounded-2xl border px-6 py-4 text-xs lg:text-sm font-medium flex items-center gap-3 shadow-lg ${
                  message.type === 'success' 
                    ? 'bg-green-900/50 border-green-500 text-green-200' 
                    : 'bg-red-900/50 border-red-500 text-red-200'
                }`}>
                  <i className={`fa-solid ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
                  <span>{message.text}</span>
                  <button 
                    onClick={() => setMessage({ type: '', text: '' })}
                    className="w-12 ml-auto text-lg p-2 bg-blue-600 text-white leading-none hover:text-white"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-5">
            <div ref={reportsSectionRef} className="rounded-3xl border border-slate-800 bg-slate-900 p-5 shadow-2xl sm:p-6">
              <div className="mb-2 flex items-start gap-4">
                <div className="rounded-2xl bg-slate-800 p-4 text-yellow-200">
                  <i className="fa-solid fa-chart-column text-lg lg:text-xl"></i>
                </div>
                <div>
                  <h2 className="text-sm lg:text-lg font-black text-yellow-200">{t('productivityTrend')}</h2>
                  <p className="mt-1 text-xs lg:text-sm text-gray-300">{t('weeklyAnalytics')}</p>
                </div>
              </div>
              <div className="h-72">
                <Line data={chartData} options={chartOptions} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}